import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { CheckCircle2, Info, TriangleAlert, X } from "lucide-react";
import { cn } from "@/lib/utils";

type ToastTone = "info" | "success" | "warning" | "danger";

interface ToastInstance {
  id: number;
  message: string;
  description?: string;
  tone: ToastTone;
  duration: number;
}

interface ToastApi {
  toast(input: { message: string; description?: string; tone?: ToastTone; duration?: number }): void;
  success(message: string, description?: string): void;
  error(message: string, description?: string): void;
  info(message: string, description?: string): void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const value = useContext(ToastContext);
  if (!value) throw new Error("useToast must be used inside <ToastProvider>");
  return value;
}

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastInstance[]>([]);

  const remove = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (input: { message: string; description?: string; tone?: ToastTone; duration?: number }) => {
      const tone = input.tone ?? "info";
      const duration = input.duration ?? (tone === "danger" ? 10_000 : 6_000);
      const id = nextId++;
      setToasts((current) => [...current, { id, message: input.message, description: input.description, tone, duration }]);
      window.setTimeout(() => remove(id), duration);
    },
    [remove]
  );

  const api = useMemo<ToastApi>(
    () => ({
      toast: push,
      success: (message, description) => push({ message, description, tone: "success" }),
      error: (message, description) => push({ message, description, tone: "danger" }),
      info: (message, description) => push({ message, description, tone: "info" }),
    }),
    [push]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={remove} />
    </ToastContext.Provider>
  );
}

const ICONS: Record<ToastTone, typeof Info> = {
  info: Info,
  success: CheckCircle2,
  warning: TriangleAlert,
  danger: TriangleAlert,
};

const TONE_CLASS: Record<ToastTone, string> = {
  info: "text-info",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
};

function ToastViewport({ toasts, onDismiss }: { toasts: ToastInstance[]; onDismiss: (id: number) => void }) {
  return (
    <div
      role="region"
      aria-label="Notifications"
      className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-full max-w-sm flex-col gap-2"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: ToastInstance; onDismiss: (id: number) => void }) {
  const Icon = ICONS[toast.tone];
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setOpen(true), 10);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <div
      className={cn(
        "pointer-events-auto rounded-md border border-line bg-surface px-3 py-2.5 shadow-md transition-all duration-[220ms] ease-out-quart",
        open ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
      )}
    >
      <div className="flex items-start gap-2.5">
        <Icon className={cn("mt-0.5 h-4 w-4 flex-shrink-0", TONE_CLASS[toast.tone])} strokeWidth={2} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-ink">{toast.message}</div>
          {toast.description && (
            <div className="mt-0.5 text-xs text-ink-muted">{toast.description}</div>
          )}
        </div>
        <button
          type="button"
          onClick={() => onDismiss(toast.id)}
          aria-label="Dismiss"
          className="rounded-sm p-0.5 text-ink-faint transition-colors hover:bg-surface-muted hover:text-ink"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
